import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";

import { IGlobalSearchResponse, IGlobalSearchResult } from "@eagerdog/interfaces";

import { apiService } from "../../services/api.service";

export type GlobalSearchStatus = "idle" | "short" | "loading" | "results" | "empty" | "error";

const DEBOUNCE_MS = 250;
const MIN_CHARS = 2;

/**
 * Mirrors the server normaliser closely enough to compare the echoed `q`
 * with what the user has typed, so an out-of-order response for an older
 * query is never shown.
 */
const normalise = (s: string) =>
  s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, " ");

export const useGlobalSearch = (limit = 5) => {
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<GlobalSearchStatus>("idle");
  const [response, setResponse] = useState<IGlobalSearchResponse | null>(null);
  const [activeIndex, setActiveIndex] = useState(-1);

  const abortRef = useRef<AbortController | null>(null);
  const timerRef = useRef<number | null>(null);
  const latestNormalised = useRef("");

  const cancelInFlight = () => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
  };

  useEffect(() => {
    const normalised = normalise(query);
    latestNormalised.current = normalised;
    cancelInFlight();

    if (query.trim().length === 0) {
      setStatus("idle");
      setResponse(null);
      setActiveIndex(-1);
      return;
    }

    if (normalised.length < MIN_CHARS) {
      setStatus("short");
      setResponse(null);
      setActiveIndex(-1);
      return;
    }

    timerRef.current = window.setTimeout(async () => {
      const controller = new AbortController();
      abortRef.current = controller;
      // Keep previous results visible while loading; only show skeletons when
      // there is nothing to keep.
      setStatus((prev) => (prev === "results" ? prev : "loading"));

      try {
        const data = await apiService.globalSearch(query.trim(), limit, controller.signal);
        if (controller.signal.aborted) return;
        // Discard responses for a query the user has already moved past.
        if (data.q !== latestNormalised.current) return;
        setResponse(data);
        setActiveIndex(-1);
        setStatus(data.results.length > 0 ? "results" : "empty");
      } catch (err) {
        if (axios.isCancel(err) || controller.signal.aborted) return;
        setResponse(null);
        setStatus("error");
      }
    }, DEBOUNCE_MS);

    return cancelInFlight;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, limit]);

  const results: IGlobalSearchResult[] = response?.results || [];

  /** Move the keyboard cursor within a list of `count` visible options, wrapping. */
  const moveActive = useCallback((delta: number, count: number) => {
    if (count === 0) return;
    setActiveIndex((i) => {
      const next = i + delta;
      if (next < 0) return count - 1;
      if (next >= count) return 0;
      return next;
    });
  }, []);

  const reset = useCallback(() => {
    cancelInFlight();
    setQuery("");
    setStatus("idle");
    setResponse(null);
    setActiveIndex(-1);
  }, []);

  return {
    query,
    setQuery,
    status,
    response,
    results,
    activeIndex,
    setActiveIndex,
    moveActive,
    reset,
  };
};
